import { FormControl } from '@angular/forms';
import { Component } from '@angular/core';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { finalize, map, Observable, startWith } from 'rxjs';
import { ClaimReassignmentService } from '../../services/claim-reassignment.service';
import { Adjuster, ClaimDetails, TransferClaimsRequest } from '../../models/claim-reassignment.model';
import { BaseTableComponent } from '../../shared/base/base-table.component';
import { formatHttpErrorMessage } from '../../shared/utils/error.utils';
import { resolveLastUpdatedBy } from '../../shared/utils/user.utils';
import { toggleAutocompletePanel } from '../../shared/utils/autocomplete-panel.utils';

export type ReassignMode = 'all' | 'excel' | null;

@Component({
  selector: 'app-claim-reassignment',
  standalone: false,
  templateUrl: './claim-reassignment.component.html',
  styleUrl: './claim-reassignment.component.css',
})
export class ClaimReassignmentComponent extends BaseTableComponent<ClaimDetails> {
  readonly displayedColumns: string[] = [
    'claimKey',
    'claimantKey',
    'insuranceType',
    'claimNumber',
    'injuredPartyName',
    'isDeactivated',
  ];

  override pageSize = 10;
  override sortBy = 'claimKey';
  override sortDir: 'asc' | 'desc' = 'asc';

  adjusters: Adjuster[] = [];
  selectedFromAdjusterID: number = -1;
  selectedToAdjusterID: number = -1;
  fromAdjusterControl = new FormControl<string | Adjuster>('');
  toAdjusterControl = new FormControl<string | Adjuster>('');
  filteredFromAdjusters$: Observable<Adjuster[]> = new Observable<Adjuster[]>();
  filteredToAdjusters$: Observable<Adjuster[]> = new Observable<Adjuster[]>();

  reassignMode: ReassignMode = null;
  potentialClaims = 0;
  activeClaims = 0;
  deactivatedClaims = 0;
  selectedFile: File | null = null;
  uploadedClaims: ClaimDetails[] = [];
  adjusterTransferID: number | null = null;
  uploadedFileName = '';
  statusMessage = '';
  uploading = false;
  transferring = false;

  get showPaginator(): boolean {
    return this.reassignMode === 'all' || (this.reassignMode === 'excel' && !!this.uploadedFileName);
  }

  get paginatorLength(): number {
    return this.reassignMode === 'excel' ? this.uploadedClaims.length : this.totalCount;
  }

  constructor(private claimReassignmentService: ClaimReassignmentService) {
    super();
  }

  override ngOnInit(): void {
    this.initAdjusterFilters();
    this.loadAdjusters();
  }

  onAdjusterFromChange(): void {
    this.clearStatus();
    this.reassignMode = null;
    this.selectedFile = null;
    this.resetGrid();
    this.adjusterTransferID = null;
    this.uploadedFileName = '';
  }

  onAdjusterToChange(): void {
    this.clearStatus();
    if (this.reassignMode === 'excel') {
      this.resetGrid();
    }
  }

  onFromAdjusterSelected(event: MatAutocompleteSelectedEvent): void {
    const adjuster = event.option.value as Adjuster;
    this.selectedFromAdjusterID = adjuster.adjusterID;
    this.onAdjusterFromChange();
  }

  onToAdjusterSelected(event: MatAutocompleteSelectedEvent): void {
    const adjuster = event.option.value as Adjuster;
    this.selectedToAdjusterID = adjuster.adjusterID;
    this.onAdjusterToChange();
  }

  onModeChange(mode: ReassignMode): void {
    this.clearStatus();
    this.resetGrid();
    this.adjusterTransferID = null;
    this.uploadedFileName = '';
    this.selectedFile = null;
    this.reassignMode = mode;

    if (!this.validateAdjusters()) {
      return;
    }

    if (mode === 'all' && this.selectedFromAdjusterID !== -1) {
      this.loadDataWrapper();
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] ?? null;
  }

  onUpload(): void {

    if (!this.validateAdjusters()) return;

    const ext = this.selectedFile!.name.split('.').pop()?.toLowerCase();
    if (ext !== 'xlsx') {
      this.errorMessage = 'Invalid file type. Only .xlsx files are allowed.';
      return;
    }

    this.uploading = true;
    this.resetGrid();
    this.clearStatus();
    const lastUpdatedBy = resolveLastUpdatedBy({ includeFullNameFallback: true });

    this.claimReassignmentService
      .uploadExcel(
        this.selectedFile!,
        this.selectedFromAdjusterID,
        this.selectedToAdjusterID,
        lastUpdatedBy
      )
      .pipe(finalize(() => (this.uploading = false)))
      .subscribe({
        next: (res) => {
          this.uploadedClaims = res.claims ?? [];
          this.totalCount = res.totalCount ?? this.uploadedClaims.length;
          this.adjusterTransferID = res.adjusterTransferID;
          this.uploadedFileName = this.selectedFile?.name ?? '';
          this.applySummaryCounts(
            this.totalCount,
            res.activeCount ?? this.uploadedClaims.filter(c => c.isDeactivated === 'No').length,
            res.deactivatedCount ?? this.uploadedClaims.filter(c => c.isDeactivated === 'Yes').length
          );
          this.pageIndex = 0;
          this.applyUploadedClaimsView(0, this.pageSize);
          this.statusMessage = `Upload status: File '${this.uploadedFileName}' uploaded!`;
        },
        error: (err) => {
          this.resetGrid();
          this.errorMessage = formatHttpErrorMessage('Upload status: Error uploading the file!', err);
        },
      });
  }

  onTransfer(): void {
    if (!this.validateAdjusters()) return;

    if (this.dataSource.data.length === 0) {
      this.errorMessage = 'No claims listed in the grid to transfer.';
      return;
    }

    this.transferring = true;
    this.clearStatus();

    const transferRequest = this.buildTransferRequest();
    if (!transferRequest) {
      this.transferring = false;
      return;
    }

    if (this.reassignMode === 'all') {
      const lastUpdatedBy = resolveLastUpdatedBy({ includeFullNameFallback: true });
      this.claimReassignmentService
        .transferAllClaims(transferRequest, lastUpdatedBy)
        .pipe(finalize(() => (this.transferring = false)))
        .subscribe({
          next: () => {
            this.statusMessage = 'Claims transferred successfully!';
            this.reassignMode = null;
            this.resetGrid();
          },
          error: (err) =>
            (this.errorMessage = formatHttpErrorMessage('Error transferring claims', err)),
        });
    } else if (this.reassignMode === 'excel') {
      if (!this.adjusterTransferID || !this.uploadedFileName) {
        this.errorMessage = 'Please upload a spreadsheet before transferring.';
        this.transferring = false;
        return;
      }
      const lastUpdatedBy = resolveLastUpdatedBy({ includeFullNameFallback: true });
      this.claimReassignmentService
        .transferClaimsOnExcel({
          ...transferRequest,
          FileName: this.uploadedFileName,
          AdjusterTransferID: this.adjusterTransferID,
        }, lastUpdatedBy)
        .pipe(finalize(() => (this.transferring = false)))
        .subscribe({
          next: () => {
            this.statusMessage = 'Claims transferred successfully!';
            this.adjusterTransferID = null;
            this.uploadedFileName = '';
            this.selectedFile = null;
            this.reassignMode = null;
            this.resetGrid();
          },
          error: (err) =>
            (this.errorMessage = formatHttpErrorMessage('Error transferring claims', err)),
        });
    }
  }

  onCancel(): void {
    this.selectedFromAdjusterID = -1;
    this.selectedToAdjusterID = -1;
    this.fromAdjusterControl.setValue('');
    this.toAdjusterControl.setValue('');
    this.reassignMode = null;
    this.adjusterTransferID = null;
    this.uploadedFileName = '';
    this.selectedFile = null;
    this.resetGrid();
    this.clearStatus();
  }

  protected override loadData(skip: number, count: number): void {
    if (this.selectedFromAdjusterID === -1) return;

    this.loading = true;

    if (this.reassignMode === 'all') {
      this.claimReassignmentService
        .getClaimsByAdjusterID(
          this.selectedFromAdjusterID,
          skip,
          count,
          this.sortBy,
          this.sortDir
        )
        .pipe(finalize(() => (this.loading = false)))
        .subscribe({
          next: (res) => {
            this.dataSource.data = res.claims ?? [];
            this.totalCount = res.totalCount ?? 0;
            this.applySummaryCounts(
              this.totalCount,
              res.activeCount ?? 0,
              res.deactivatedCount ?? 0
            );
          },
          error: (err) =>
            (this.errorMessage = formatHttpErrorMessage('Failed to load claims', err)),
        });
      return;
    }

    if (this.reassignMode === 'excel') {
      this.applyUploadedClaimsView(skip, count);
      this.loading = false;
      return;
    }

    this.loading = false;
  }

  clearFromAdjuster(event: Event): void {
    event.stopPropagation();
    this.fromAdjusterControl.setValue('');
    this.selectedFromAdjusterID = -1;
    this.onAdjusterFromChange();
  }

  clearToAdjuster(event: Event): void {
    event.stopPropagation();
    this.toAdjusterControl.setValue('');
    this.selectedToAdjusterID = -1;
    this.onAdjusterToChange();
  }

  openOrClosePanel(event: Event, trigger: MatAutocompleteTrigger): void {
    toggleAutocompletePanel(event, trigger);
  }
  
  private loadAdjusters(): void {
    this.loading = true;
    this.claimReassignmentService
      .getAllAdjusters()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          this.adjusters = res.adjusters ?? [];
          this.fromAdjusterControl.setValue(this.fromAdjusterControl.value);
          this.toAdjusterControl.setValue(this.toAdjusterControl.value);
        },
        error: (err) => (this.errorMessage = formatHttpErrorMessage('Failed to load adjusters', err)),
      });
  }

  private resetGrid(): void {
    this.uploadedClaims = [];
    this.dataSource.data = [];
    this.totalCount = 0;
    this.applySummaryCounts(0, 0, 0);
    this.pageIndex = 0;
  }

  private validateAdjusters(): boolean {
    if (this.selectedFromAdjusterID === -1) {
      this.errorMessage = "Please choose 'Adjuster From'.";
      return false;
    }
    if (this.selectedToAdjusterID === -1) {
      this.errorMessage = "Please choose 'Adjuster To'.";
      return false;
    }
    if (this.selectedFromAdjusterID === this.selectedToAdjusterID) {
      this.errorMessage = "The 'Adjuster From' and 'Adjuster To' cannot be the same.";
      return false;
    }
    return true;
  }

  private buildTransferRequest(): TransferClaimsRequest | null {
    const fromAdjuster = this.adjusters.find((a) => a.adjusterID === this.selectedFromAdjusterID);
    const toAdjuster = this.adjusters.find((a) => a.adjusterID === this.selectedToAdjusterID);

    if (!fromAdjuster || !toAdjuster) {
      this.errorMessage = 'Unable to resolve selected adjusters. Please reselect and try again.';
      return null;
    }

    const fromFirstName = (fromAdjuster.firstName ?? '').trim();
    const fromLastName = (fromAdjuster.lastName ?? '').trim();
    const toFirstName = (toAdjuster.firstName ?? '').trim();
    const toLastName = (toAdjuster.lastName ?? '').trim();

    if (!fromFirstName || !fromLastName || !toFirstName || !toLastName) {
      this.errorMessage = 'Adjuster names are missing for transfer payload. Please reselect adjusters and try again.';
      return null;
    }
    
    const fromKey = Number(fromAdjuster.adjusterKey) || 0;
    const toKey = Number(toAdjuster.adjusterKey) || 0;

    return {
      FromAdjusterID: this.selectedFromAdjusterID,
      ToAdjusterID: this.selectedToAdjusterID,
      FromAdjusterKey: fromKey,
      ToAdjusterKey: toKey,
      FromAdjusterFirstName: fromFirstName,
      FromAdjusterLastName: fromLastName,
      ToAdjusterFirstName: toFirstName,
      ToAdjusterLastName: toLastName,
      ClaimsToBeTransferredCount: this.totalCount,
      AdjusterTransferID: this.adjusterTransferID ?? undefined,
      FileName: this.uploadedFileName || undefined
    };
  }

  private initAdjusterFilters(): void {
    this.filteredFromAdjusters$ = this.fromAdjusterControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filterAdjusters(value))
    );

    this.filteredToAdjusters$ = this.toAdjusterControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filterAdjusters(value))
    );
  }

  private filterAdjusters(value: string | Adjuster | null): Adjuster[] {
    const searchText =
      typeof value === 'string'
        ? value.toLowerCase()
        : (value?.adjusterText ?? '').toLowerCase();

    return this.adjusters.filter(
      (adjuster) =>
        adjuster.adjusterID !== -1 &&
        adjuster.adjusterText.toLowerCase().includes(searchText)
    );
  }

  private clearStatus(): void {
    this.errorMessage = '';
    this.statusMessage = '';
  }

  private applySummaryCounts(total: number, active: number, deactivated: number): void {
    this.potentialClaims = total;
    this.activeClaims = active;
    this.deactivatedClaims = deactivated;
  }

  private applyUploadedClaimsView(skip: number, count: number): void {
    const sorted = [...this.uploadedClaims].sort((a, b) => {
      const field = this.sortBy as keyof ClaimDetails;
      const left = String(a[field] ?? '').toLowerCase();
      const right = String(b[field] ?? '').toLowerCase();

      const cmp = left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });
      return this.sortDir === 'desc' ? -cmp : cmp;
    });

    this.dataSource.data = sorted.slice(skip, skip + count);
  }

  displayAdjusterText = (adjuster: Adjuster | string | null): string => {
    if (!adjuster) {
      return '';
    }

    return typeof adjuster === 'string' ? adjuster : adjuster.adjusterText;
  };
}
